import { Link } from "react-router-dom";
import { ArrowRight, FileText, Layout, Ruler, Cog, Zap, Droplets } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

export const SystemsDesign = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="Systems Design | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Professional CAD drawings, 3D renderings, and technical planning for heating and plumbing systems. Expert engineering solutions for residential and commercial projects."
        canonical="/systems-design"
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-sage-500/20 text-sage-300 px-6 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-md border border-sage-500/30">
              Professional Engineering & Design
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Heating & Plumbing Systems Design
            </h1>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Comprehensive technical planning, CAD drawings, 3D renderings, and engineering solutions for your heating and plumbing projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-lg px-10 py-7">
                  Request Design Consultation
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </Link>
              <a href="tel:03337890987">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-white hover:bg-white hover:text-navy-900 text-lg px-10 py-7 backdrop-blur-md">
                  Call Our Engineers
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Design Services */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Professional Design Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From concept to construction, we provide complete technical documentation and engineering solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <FileText className="h-12 w-12 text-sage-400" />,
                title: "CAD Drawings & Blueprints",
                description: "Detailed technical drawings for all plumbing and heating systems, fully compliant with regulations."
              },
              {
                icon: <Layout className="h-12 w-12 text-sage-400" />,
                title: "3D System Renderings",
                description: "Visualize your systems before installation with professional 3D modeling and rendering."
              },
              {
                icon: <Cog className="h-12 w-12 text-sage-400" />,
                title: "Heat Pump Layout Design",
                description: "Optimized heat pump positioning and ductwork design for maximum efficiency."
              },
              {
                icon: <Droplets className="h-12 w-12 text-sage-400" />,
                title: "Plumbing System Plans",
                description: "Complete plumbing schematics including water supply, drainage, and fixtures."
              },
              {
                icon: <Zap className="h-12 w-12 text-sage-400" />,
                title: "Energy Calculations",
                description: "Comprehensive energy efficiency analysis and performance predictions."
              },
              {
                icon: <Ruler className="h-12 w-12 text-sage-400" />,
                title: "Compliance Documentation",
                description: "All necessary regulatory documentation and building permit applications."
              }
            ].map((service) => (
              <Card key={service.title} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-300">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Design Gallery */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Professional Technical Plans
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Examples of our detailed engineering drawings and 3D visualizations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="overflow-hidden bg-white/5 backdrop-blur-md border-white/10 hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1721244654392-9c912a6eb236?w=800&q=80" 
                alt="Architectural blueprint with windows"
                className="w-full h-64 object-cover"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">Architectural Blueprints</h3>
                <p className="text-gray-300">Detailed building plans with complete system integration</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/5 backdrop-blur-md border-white/10 hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1698846296220-e44d9d4b9100?w=800&q=80" 
                alt="Person drawing floor plan"
                className="w-full h-64 object-cover"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">Custom Floor Plans</h3>
                <p className="text-gray-300">Hand-crafted technical drawings tailored to your project</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/5 backdrop-blur-md border-white/10 hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1664681545163-1caf9c1fffa5?w=800&q=80" 
                alt="Plumbing pipes system"
                className="w-full h-64 object-cover"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">Plumbing System Design</h3>
                <p className="text-gray-300">3D visualization of pipe routing and fixture placement</p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden bg-white/5 backdrop-blur-md border-white/10 hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=800&q=80" 
                alt="CAD software on computer"
                className="w-full h-64 object-cover"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">CAD Software Design</h3>
                <p className="text-gray-300">Professional engineering software for precision planning</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Our Design Process
              </h2>
              <p className="text-xl text-gray-300">
                From initial consultation to final construction documents
              </p>
            </div>

            <div className="space-y-8">
              {[
                {
                  step: "01",
                  title: "Site Assessment & Consultation",
                  description: "We visit your property to understand requirements, take measurements, and discuss your needs."
                },
                {
                  step: "02",
                  title: "Conceptual Design",
                  description: "Initial sketches and system layouts to establish the overall approach and feasibility."
                },
                {
                  step: "03",
                  title: "Detailed Engineering",
                  description: "Complete CAD drawings, 3D models, energy calculations, and technical specifications."
                },
                {
                  step: "04",
                  title: "Documentation & Permits",
                  description: "All regulatory documentation, building permits, and compliance certificates prepared."
                },
                {
                  step: "05",
                  title: "Construction Support",
                  description: "On-site support during installation to ensure plans are executed correctly."
                }
              ].map((item) => (
                <Card key={item.step} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                  <CardContent className="p-8 flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-sage-500/20 backdrop-blur-md border-2 border-sage-400 flex items-center justify-center">
                        <span className="text-2xl font-bold text-sage-400">{item.step}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-300 text-lg">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact our engineering team for professional design and planning services
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-xl px-12 py-8">
                Request a Design Consultation
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
